import React, { useState, useEffect } from 'react';

const DEFAULT_STATISTICS = {
  homepage: {
    teamMembers: '7+',
    hoursStreamed: '13,000+',
    charityRaised: '$10K+',
    communityEvents: 'Weekly'
  },
  charity: {
    totalCharityGoal: '$12,000',
    nextEventGoal: '$5,000',
    eventDescription: 'Holiday Charity Stream Marathon'
  }
};

function StatisticsEditor({ data, onSave, showToast }) {
  const [stats, setStats] = useState(DEFAULT_STATISTICS);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setStats({
        homepage: { ...DEFAULT_STATISTICS.homepage, ...data.homepage },
        charity: { ...DEFAULT_STATISTICS.charity, ...data.charity }
      });
    }
  }, [data]);

  const handleChange = (section, field, value) => {
    setStats(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await onSave(stats);
    setSaving(false);
    if (result.success) {
      setHasChanges(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--forest-800)', margin: 0 }}>
            Statistics
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--forest-600)', marginTop: '0.25rem' }}>
            Update the numbers displayed on the homepage and charity pages
          </p>
        </div>
        <button
          className="btn-editor btn-editor-primary"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{ opacity: hasChanges ? 1 : 0.5 }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Homepage Stats Section */}
      <div className="settings-section">
        <h3 className="settings-title">Homepage Statistics</h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--forest-600)', marginBottom: '1.5rem' }}>
          These appear in the statistics grid on the homepage
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Team Members</label>
            <input
              type="text"
              className="form-input"
              value={stats.homepage.teamMembers}
              onChange={(e) => handleChange('homepage', 'teamMembers', e.target.value)}
              placeholder="7+"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Hours Streamed</label>
            <input
              type="text"
              className="form-input"
              value={stats.homepage.hoursStreamed}
              onChange={(e) => handleChange('homepage', 'hoursStreamed', e.target.value)}
              placeholder="13,000+"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Charity Raised</label>
            <input
              type="text"
              className="form-input"
              value={stats.homepage.charityRaised}
              onChange={(e) => handleChange('homepage', 'charityRaised', e.target.value)}
              placeholder="$10K+"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Community Events</label>
            <input
              type="text"
              className="form-input"
              value={stats.homepage.communityEvents}
              onChange={(e) => handleChange('homepage', 'communityEvents', e.target.value)}
              placeholder="Weekly"
            />
          </div>
        </div>

        {/* Homepage Preview */}
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--forest-500)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Preview
          </div>
          <div className="stats-preview">
            <div className="stats-preview-item">
              <div className="stats-preview-value">{stats.homepage.teamMembers}</div>
              <div className="stats-preview-label">Team Members</div>
            </div>
            <div className="stats-preview-item">
              <div className="stats-preview-value">{stats.homepage.hoursStreamed}</div>
              <div className="stats-preview-label">Hours Streamed</div>
            </div>
            <div className="stats-preview-item">
              <div className="stats-preview-value">{stats.homepage.charityRaised}</div>
              <div className="stats-preview-label">Charity Raised</div>
            </div>
            <div className="stats-preview-item">
              <div className="stats-preview-value">{stats.homepage.communityEvents}</div>
              <div className="stats-preview-label">Community Events</div>
            </div>
          </div>
        </div>
      </div>

      {/* Charity Stats Section */}
      <div className="settings-section">
        <h3 className="settings-title">Charity Page Statistics</h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--forest-600)', marginBottom: '1.5rem' }}>
          These appear on the charity page goal section
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Total Charity Goal</label>
            <input
              type="text"
              className="form-input"
              value={stats.charity.totalCharityGoal}
              onChange={(e) => handleChange('charity', 'totalCharityGoal', e.target.value)}
              placeholder="$12,000"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Next Event Goal</label>
            <input
              type="text"
              className="form-input"
              value={stats.charity.nextEventGoal}
              onChange={(e) => handleChange('charity', 'nextEventGoal', e.target.value)}
              placeholder="$5,000"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Event Description</label>
            <input
              type="text"
              className="form-input"
              value={stats.charity.eventDescription}
              onChange={(e) => handleChange('charity', 'eventDescription', e.target.value)}
              placeholder="Holiday Charity Stream Marathon"
            />
          </div>
        </div>

        {/* Charity Preview */}
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--forest-500)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Preview
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
            borderRadius: '1rem',
            padding: '1.5rem',
            color: 'white'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>{stats.charity.totalCharityGoal}</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Goal</div>
              </div>
              <div>
                <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>{stats.charity.nextEventGoal}</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Next Event Goal</div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', opacity: 0.9 }}>
              {stats.charity.eventDescription}
            </div>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="settings-section" style={{ background: 'var(--forest-50)' }}>
        <h3 className="settings-title">Tips</h3>
        <ul style={{ fontSize: '0.875rem', color: 'var(--forest-600)', paddingLeft: '1.25rem', margin: 0 }}>
          <li style={{ marginBottom: '0.5rem' }}>Use short, impactful values that fit on one line</li>
          <li style={{ marginBottom: '0.5rem' }}>Include + symbols to show growth (e.g., "7+", "13,000+")</li>
          <li style={{ marginBottom: '0.5rem' }}>Use K for thousands (e.g., "$10K+" instead of "$10,000+")</li>
          <li>Keep event descriptions brief but descriptive</li>
        </ul>
      </div>
    </div>
  );
}

export default StatisticsEditor;
